import React, { useState, useEffect, type ReactNode } from "react";
import CartBannerHeader from "./CartBannerHeader";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import API_ENDPOINTS from '../../api/apiConfig';
import { useUser } from "../../context/UserContext";
import '../../assets/css/theme.css';
import '../../assets/css/cart.css';
import type { CartItem, OrderPayloadItem } from "../../types";
import { FaCreditCard, FaMoneyBillWave, FaTag, FaCircleCheck, FaXmark } from "react-icons/fa6";

interface PaymentOption {
  label: string;
  value: string;
  icon: ReactNode;
  iconColor: string;
  description: string;
  tag: string | null;
}

interface PaymentMethodSelectionProps {
  onSelect?: (value: string) => void;
  defaultValue?: string;
}

const paymentOptions: PaymentOption[] = [
  {
    label: "Razorpay",
    value: "razorpay",
    icon: <FaCreditCard size={22} />,
    iconColor: "#4285F4",
    description: "UPI, Cards, Net Banking & Wallets",
    tag: "Recommended",
  },
  {
    label: "Cash on Delivery",
    value: "cod",
    icon: <FaMoneyBillWave size={22} />,
    iconColor: "#7a9e6e",
    description: "Pay with cash when your order arrives",
    tag: null,
  },
];

const PaymentMethodSelection = ({ onSelect, defaultValue }: PaymentMethodSelectionProps) => {
  const [selected, setSelected]       = useState<string>(defaultValue || "");
  const [cartTotal, setCartTotal]     = useState<number>(0);
  const [placingOrder, setPlacingOrder] = useState(false);
  const { user }                      = useUser();
  const [cartItems, setCartItems]     = useState<OrderPayloadItem[]>([]);
  const [addressId, setAddressId]     = useState<number | null>(null);

  // ✅ Coupon state
  const [couponCode, setCouponCode]         = useState("");
  const [couponApplied, setCouponApplied]   = useState(false);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [couponError, setCouponError]       = useState("");
  const [discountRate, setDiscountRate]     = useState(0);
  const [appliedCouponCode, setAppliedCouponCode] = useState("");
  const [appliedVendorId, setAppliedVendorId]     = useState<number | string | null>(null);

  const discountAmount = cartTotal * (discountRate / 100);
  const finalAmount    = cartTotal - discountAmount;

  useEffect(() => {
    let cart: CartItem[] = [];
    try {
      cart = JSON.parse(localStorage.getItem("cart") || 'null') || [];
    } catch { cart = []; }

    const getDiscountedPrice = (item: CartItem): number => {
      if (item.discount_percentage && Number(item.discount_percentage) > 0) {
        return Number(item.price) - (Number(item.price) * Number(item.discount_percentage)) / 100;
      }
      return Number(item.price);
    };

    const subtotal = cart.reduce((sum, item) => {
      const hasVendorDiscount =
        localStorage.getItem("authToken") && user?.created_by && Number(item.discount_percentage) > 0;
      const pricePerItem = hasVendorDiscount ? getDiscountedPrice(item) : Number(item.price);
      return sum + pricePerItem * Number(item.quantity);
    }, 0);

    setCartTotal(subtotal);

    const items = cart.map((item) => {
      const product_id         = item.p_id !== undefined ? Number(item.p_id) : Number(item.id);
      const size               = typeof item.size === "string" ? item.size : String(item.size);
      const quantity           = Number(item.quantity);
      const product_price      = Number(item.price);
      const discount_percentage = Number(item.discount_percentage) || 0;
      const price              = getDiscountedPrice(item);
      const total_price        = price * quantity;
      return { product_id, size, quantity, product_price, discount_percentage, price, total_price };
    });
    setCartItems(items);

    try {
      const selectedAddress = JSON.parse(localStorage.getItem("selectedAddress") || 'null');
      setAddressId(selectedAddress?.address_id || selectedAddress?.id || null);
    } catch { setAddressId(null); }
  }, []);

  const handleSelect = (value: string) => {
    setSelected(value);
    if (onSelect) onSelect(value);
  };

  // ✅ Apply coupon
  const handleApplyCoupon = async () => {
    const code = couponCode.trim();
    if (!code) {
      setCouponError("Enter a coupon code");
      return;
    }

    const token = localStorage.getItem("authToken");
    if (!token) {
      toast.error("Login required to apply coupon.");
      return;
    }

    setApplyingCoupon(true);
    setCouponError("");

    try {
      const res = await fetch(API_ENDPOINTS.APPLY_COUPON, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ coupon_code: code }),
      });
      const data = await res.json();

      if (data.status === true) {
        setCouponApplied(true);
        setDiscountRate(Number(data.discount_rate) || 0);
        setAppliedCouponCode(data.coupon_code || code);
        setAppliedVendorId(data.vendor_id ?? null);
        toast.success(`Coupon applied! ${Number(data.discount_rate).toFixed(0)}% off`);
      } else {
        setCouponApplied(false);
        setDiscountRate(0);
        setAppliedCouponCode("");
        setAppliedVendorId(null);
        setCouponError(data.message || "Invalid coupon code");
      }
    } catch {
      setCouponApplied(false);
      setDiscountRate(0);
      setAppliedCouponCode("");
      setAppliedVendorId(null);
      setCouponError("Something went wrong. Try again.");
    } finally {
      setApplyingCoupon(false);
    }
  };

  // ✅ Remove coupon
  const handleRemoveCoupon = () => {
    setCouponApplied(false);
    setDiscountRate(0);
    setAppliedCouponCode("");
    setAppliedVendorId(null);
    setCouponCode("");
    setCouponError("");
  };

  // ✅ Cancel a pending Razorpay order on the backend (called when user closes the modal
  // without completing payment, so the earlier-created "order entry" doesn't stay valid/pending)
  const cancelPendingRazorpayOrder = async ({ user_order_id }: { user_order_id: number | string }) => {
    const token = localStorage.getItem("authToken");
    if (!token || !user_order_id) return;

    try {
      await fetch(API_ENDPOINTS.CANCEL_ORDER_RZP, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ user_order_id}),
      });
    } catch {
      // Silent fail — a backend sweep job for stale/expired pending orders should
      // act as a safety net in case this call doesn't reach the server.
    }
  };

  const handlePlaceOrder = async () => {
    if (!selected)     { toast.error("Please select a payment method."); return; }
    if (!addressId)    { toast.error("No address selected."); return; }
    if (!cartItems.length) { toast.error("Your cart is empty."); return; }

    setPlacingOrder(true);
    const token = localStorage.getItem("authToken");
    if (!token) { toast.error("Login required."); setPlacingOrder(false); return; }

    // ✅ Coupon fields — real values when applied, blank/zero otherwise
    const couponPayload = {
      coupon_code: couponApplied ? appliedCouponCode : "",
      coupon_discount_rate: couponApplied ? discountRate : 0,
      discount_amount: couponApplied ? discountAmount : 0,
      vendor_id: couponApplied ? appliedVendorId : null,
    };

    try {
      if (selected.toUpperCase() === "RAZORPAY") {
        const orderRes = await fetch(API_ENDPOINTS.CREATE_ORDER_RZP, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({
            payment_method: "RAZORPAY",
            total_amount: Number(finalAmount),
            items: cartItems,
            address_id: Number(addressId),
            ...couponPayload,
          }),
        });

        const orderData = await orderRes.json();
        if (!orderData.status || !orderData.data) {
          toast.error(orderData.message || "Failed to create order.");
          setPlacingOrder(false);
          return;
        }

        const {
          razorpay_order_id, razorpay_key_id, amount, currency,
          user_order_id, pu_name, pu_email, pu_contact_no, address,
        } = orderData.data;

        // Track whether verification/success handler actually ran,
        // so we know ondismiss is firing because the user backed out.
        let paymentHandled = false;

        const options: RazorpayOptions = {
          key: razorpay_key_id,
          amount,
          currency,
          name: "Fashion Frame",
          description: "Payment for your order",
          order_id: razorpay_order_id,
          prefill: { name: pu_name, email: pu_email, contact: pu_contact_no },
          notes: { address: address || "", order_amount: amount, user_order_id },
          theme: { color: "#c9a96e" },
          handler: async function (response: RazorpayResponse) {
            paymentHandled = true;
            try {
              const verifyRes = await fetch(API_ENDPOINTS.VERIFY_ORDER_RZP, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                body: JSON.stringify({
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  user_order_id,
                }),
              });
              const verifyData = await verifyRes.json();
              if (verifyData.status) {
                toast.success("Payment successful!");
                localStorage.removeItem("cart");
                setTimeout(() => { window.location.href = "/summary"; }, 1500);
              } else {
                toast.error("Payment verification failed.");
              }
            } catch {
              toast.error("Verification failed.");
            }
          },
          // ✅ Fires when the user closes the Razorpay popup WITHOUT completing payment.
          // This is the hook that lets us invalidate/cancel the pending order entry
          // that was created on the backend in CREATE_ORDER_RZP above.
          modal: {
            ondismiss: async function () {
              setPlacingOrder(false);
              if (!paymentHandled) {
                toast.info("Payment cancelled.");
                await cancelPendingRazorpayOrder({
                  user_order_id
                });
              }
            },
          },
        };

        const rzp = new window.Razorpay(options);
        rzp.on("payment.failed", async () => {
          toast.error("Payment failed. Please try again.");
          await cancelPendingRazorpayOrder({
            user_order_id
          });
        });
        rzp.open();
        setPlacingOrder(false);
        return;
      }

      // COD Flow
      const codRes = await fetch(API_ENDPOINTS.CREATE_USER_ORDER, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          payment_method: "COD",
          total_amount: Number(finalAmount),
          items: cartItems,
          address_id: Number(addressId),
          ...couponPayload,
        }),
      });

      const codData = await codRes.json();
      if (codRes.ok && (codData.status || codData.success)) {
        toast.success("Order placed successfully!");
        localStorage.removeItem("cart");
        setTimeout(() => { window.location.href = "/summary"; }, 1500);
      } else {
        toast.error(codData.message || "Failed to place order.");
      }
    } catch {
      toast.error("Something went wrong.");
    }

    setPlacingOrder(false);
  };

  return (
    <main className="ffc-page">
      <CartBannerHeader currentStep={3} />

      <section className="ffc-body">
        <div className="ffc-container">
          <div className="ffc-layout">

            {/* ── Left: coupon + payment methods ── */}
            <div>
              {/* Coupon */}
              <div className={`ffc-coupon${couponApplied ? " is-applied" : ""}`}>
                <div className="ffc-coupon-head">
                  <FaTag size={14} color={couponApplied ? "#2e7d32" : "#9a7635"} />
                  <span>Coupon code</span>
                </div>

                {couponApplied ? (
                  <div className="ffc-coupon-ok">
                    <FaCircleCheck size={16} color="#2e7d32" />
                    <span>{appliedCouponCode} applied — {discountRate.toFixed(0)}% off</span>
                    <button
                      type="button"
                      className="ffc-coupon-remove"
                      onClick={handleRemoveCoupon}
                      aria-label="Remove coupon"
                    >
                      <FaXmark size={13} />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="ffc-coupon-row">
                      <input
                        type="text"
                        className="ffc-coupon-input"
                        placeholder="Enter coupon code"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        disabled={applyingCoupon}
                      />
                      <button
                        type="button"
                        className="ffc-coupon-apply"
                        onClick={handleApplyCoupon}
                        disabled={applyingCoupon}
                      >
                        {applyingCoupon ? "..." : "Apply"}
                      </button>
                    </div>
                    {couponError && <div className="ffc-coupon-error">{couponError}</div>}
                  </>
                )}
              </div>

              {/* Payment options */}
              <div className="ffc-section-head">
                <h2 className="ffc-section-title">Payment method</h2>
              </div>

              <div role="radiogroup" aria-label="Payment method">
                {paymentOptions.map((option) => {
                  const isSelected = selected === option.value;
                  return (
                    <div
                      key={option.value}
                      className={`ffc-select ffc-pay${isSelected ? " is-selected" : ""}`}
                      onClick={() => handleSelect(option.value)}
                      role="radio"
                      aria-checked={isSelected}
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleSelect(option.value); } }}
                    >
                      <div className="ffc-pay-icon" style={{ color: option.iconColor }}>
                        {option.icon}
                      </div>
                      <div>
                        <div className="ffc-pay-name">
                          {option.label}
                          {option.tag && <span className="ffc-pay-tag">{option.tag}</span>}
                        </div>
                        <div className="ffc-pay-desc">{option.description}</div>
                      </div>
                      <span className="ffc-radio" aria-hidden="true" />
                    </div>
                  );
                })}
              </div>

              <div className="ffc-actions">
                <button
                  type="button"
                  className="ffh-btn ffh-btn--outline"
                  onClick={() => window.history.back()}
                  disabled={placingOrder}
                >
                  ← Back
                </button>
              </div>
            </div>

            {/* ── Right: total + place order ── */}
            <aside className="ffc-side">
              <div className="ffc-card">
                <h3 className="ffc-card-title">Order total</h3>

                <div className="ffc-row">
                  <span>Subtotal</span>
                  <span>₹{cartTotal.toFixed(2)}</span>
                </div>

                {couponApplied && (
                  <div className="ffc-row ffc-row--discount">
                    <span>Coupon ({discountRate.toFixed(0)}%)</span>
                    <span>− ₹{discountAmount.toFixed(2)}</span>
                  </div>
                )}

                <div className="ffc-row">
                  <span>Shipping</span>
                  <span className="ffc-free">FREE</span>
                </div>

                <div className="ffc-divider" />

                <div className="ffc-row ffc-row--total">
                  <span>Total</span>
                  <span>₹{finalAmount.toFixed(2)}</span>
                </div>

                <button
                  type="button"
                  className="ffh-btn ffc-btn--block"
                  style={{ marginTop: 24 }}
                  disabled={!selected || placingOrder}
                  onClick={handlePlaceOrder}
                >
                  {placingOrder ? "Placing order…" : "Place order"}
                </button>

                <div className="ffc-secure">
                  🔒 All transactions are secured &amp; encrypted
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <ToastContainer
        position="top-right"
        toastStyle={{
          fontFamily: "'Jost', sans-serif",
          fontSize: "0.85rem",
          letterSpacing: "0.04em",
          borderRadius: "3px",
        }}
      />
    </main>
  );
};

export default PaymentMethodSelection;